import { Injectable } from '@angular/core';
import { AirportCode } from '@com-delta-omnichannel/mach-core-js';
import { FlightStatusForm } from '../../models/interfaces/flight-status-form.interface';
import { AirportConfigUtil } from './airport-config.util';

@Injectable({
  providedIn: 'root'
})
export class FlightStatusAirportService {
  public airportCodeInstance?: AirportCode;
  private lastClickedTrigger: 'origin' | 'destination' | null = null;
  private selectionChangeCallback?: (isSwap?: boolean) => void;

  initializeAirportCode(
    onOriginClick: () => void,
    onDestinationClick: () => void,
    content?: FlightStatusForm
  ): void {
    setTimeout(() => {
      const container = document.getElementById('flight-status-airport-code');
      if (container) {
        const config = AirportConfigUtil.createAirportCodeConfig(content);
        this.airportCodeInstance = new AirportCode(config);
        this.setupClickHandlers(onOriginClick, onDestinationClick);
      }
    }, 100);
  }

  private setupClickHandlers(
    onOriginClick: () => void,
    onDestinationClick: () => void
  ): void {
    if (this.airportCodeInstance) {
      this.airportCodeInstance.onOriginClick(() => {
        this.setLastClickedTrigger('origin');
        onOriginClick();
      });

      this.airportCodeInstance.onDestinationClick(() => {
        this.setLastClickedTrigger('destination');
        onDestinationClick();
      });

      this.airportCodeInstance.onSwap(() => {
        if (this.selectionChangeCallback) {
          this.selectionChangeCallback(true);
        }
      });
    }
  }

  private setLastClickedTrigger(trigger: 'origin' | 'destination'): void {
    this.lastClickedTrigger = trigger;
  }

  getLastClickedTrigger(): 'origin' | 'destination' | null {
    return this.lastClickedTrigger;
  }

  setSelectedAirport(code: string): void {
    // Show only the airport code, not the full name
    if (this.lastClickedTrigger === 'origin') {
      this.setOriginAirport(code);
    } else if (this.lastClickedTrigger === 'destination') {
      this.setDestinationAirport(code);
    } else {
      this.setOriginAirport(code);
    }

    // Trigger selection change callback
    if (this.selectionChangeCallback) {
      this.selectionChangeCallback();
    }
  }

  onAirportSelectionChange(callback: (isSwap?: boolean) => void): void {
    this.selectionChangeCallback = callback;
  }

  setOriginAirport(value: string): void {
    this.airportCodeInstance?.setOriginValue(value);
    // Trigger selection change callback
    if (this.selectionChangeCallback) {
      this.selectionChangeCallback();
    }
  }

  setDestinationAirport(value: string): void {
    this.airportCodeInstance?.setDestinationValue(value);
    // Trigger selection change callback
    if (this.selectionChangeCallback) {
      this.selectionChangeCallback();
    }
  }

  getOriginValue(): string {
    return this.airportCodeInstance?.getOriginValue() || '';
  }

  getDestinationValue(): string {
    return this.airportCodeInstance?.getDestinationValue() || '';
  }

  getSelection(): { origin: string; destination: string } {
    return (
      this.airportCodeInstance?.getSelection() || {
        origin: '',
        destination: ''
      }
    );
  }

  clearOrigin(): void {
    this.airportCodeInstance?.clearOrigin();
  }

  clearDestination(): void {
    this.airportCodeInstance?.clearDestination();
  }

  clearAll(): void {
    this.airportCodeInstance?.clearAll();
    // Also clear error states when clearing values
    this.setOriginError(false);
    this.setDestinationError(false);
  }

  swapAirports(): void {
    this.airportCodeInstance?.swapAirports();
  }

  setOriginError(hasError: boolean): void {
    this.airportCodeInstance?.setOriginError(hasError);
  }

  setDestinationError(hasError: boolean): void {
    this.airportCodeInstance?.setDestinationError(hasError);
  }

  destroy(): void {
    this.airportCodeInstance?.destroy();
  }
}
